import { useState, Fragment  } from "react"

import { Card, Space, Typography, Flex } from "antd"

import { Resource, Scope } from "@/constants/permission"

import classes from "./RequestQuoteInfo.module.scss"

import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useFuelTypeSelectListQuery } from "@/hook/Dictionaries/useFuelTypeQuery"
import { usePermission } from "@/hook/usePermission"

import { BulkOrderLogChangeType, BulkOrderLogEvent, IBulkOrder, IBulkOrderFuel, IBulkOrderFuelLog, IBulkOrderLog } from "@/types/IBulkOrder"
import { IFuelType } from "@/types/IFuelType"
import { DeliveryType } from "@/types/IOrder"

import { formatDateTime, onlyDateFormat } from "@/utils/formatDate"

const { Text } = Typography

export interface IRequestQuoteInfoProps {
  bulkOrder: IBulkOrder
  textColor?: string
}

export const RequestQuoteInfo = (props: IRequestQuoteInfoProps) => {
  const {
    bulkOrder,
    textColor
  } = props
  const { hasPermission } = usePermission()
  const [isAdmin, setIsAdmin] = useState<boolean>(hasPermission(Resource.RequestQuotes, Scope.AllActions))
  const { data: depots, isLoading: depotsIsLoading } = useDepotSelectListQuery()
  const { data: fuelTypes, isLoading: fuelTypesIsLoading } = useFuelTypeSelectListQuery()

  const getDate = (date: string) => {
      return formatDateTime(date, onlyDateFormat)
  }

  const getHeader = (id: number, createdOn: string, customerName: string) => {
      return `№ ${id} от ${getDate(createdOn)}${isAdmin ? ` ${customerName}`: ""}`
  }

  const getPrice = (offeredPrice?: number) => {
      return offeredPrice ? `${offeredPrice} руб` : "на дату отгрузки" 
  }

  const getValueDate = (value: string, changeType: BulkOrderLogChangeType) => {
    return baseText(getDate(value), isChangeValue(changeType) ? "warning" : undefined)
  }

  const getCreatedFuelLogs = () => {
    const log = bulkOrder.logs.find(x => x.event ===  BulkOrderLogEvent.Created)

    if(!log) {
      return []
    }
   
    return (JSON.parse(log.currentValue).Fuels as IBulkOrderFuelLog[])
  }

  const getValueDepot = () => {
    const log = getCreatedFuelLogs()[0]
    return baseText(bulkOrder.depotName, log && log.DepotId !== bulkOrder.depotId ? "warning" : undefined)
  }

  const getValueFuelType = (fuelType: IFuelType, id: number) => {
    const log = getCreatedFuelLogs().find(x => x.Id === id)
    if(!log) {
      return fuelType.name
    }

    return baseText(fuelType.name, log.FuelTypeId !== fuelType.id ? "warning" : undefined)
  }


  const getValueQuantity = (value: number, id: number) => {
    const log = getCreatedFuelLogs().find(x => x.Id === id)
    if(!log) {
      return `${String(value)}тн`
    }

    return baseText(`${String(value)}тн`, log.Quantity !== value ? "warning" : undefined)
  }

  const getValueOfferedPrice = (value: number | undefined, id: number) => {
    const log = getCreatedFuelLogs().find(x => x.Id === id)
    if(!log) {
      return getPrice(value)
    }

    return baseText(getPrice(value), log.OfferedPrice != value ? "warning" : undefined)
  }

  const getValueNewFuel = (bulkOrderFuel: IBulkOrderFuel) => {
    const text = `${bulkOrder.depotName} ${bulkOrderFuel.fuel.fuelType.name} ${bulkOrderFuel.quantity}тн по цене: ${getPrice(bulkOrderFuel.offeredPrice)}`
    return baseText(text, "warning")
  }

  const baseText = (value: string, type: "secondary" | "warning" | undefined) => 
    <Text style={{ color: textColor ?? "" }} type={!textColor ? type : undefined} className={classes[type ? "text-strong" : ""]}>
      {value}
    </Text>

  
  const isChangeValue = (changeType: BulkOrderLogChangeType, bulkOrderFuelId?: number) => {

    if (changeType === BulkOrderLogChangeType.ChangeFuels) {
      return false
    }

    if (bulkOrder.logs?.some(x => x.event === BulkOrderLogEvent.Updated && x.changesTypes.some(y => y === changeType))) {
      return true
    }
    
    return false
  }

 const isNewFuel = (bulkOrderFuelId: number) => {
    let isChange = false
    bulkOrder.logs.filter(x => x.event ===  BulkOrderLogEvent.Updated && x.changesTypes.some(y => y === BulkOrderLogChangeType.ChangeFuels)).forEach(x => {

      if( x.prevValue && x.currentValue) {
        const prev = (JSON.parse(x.prevValue).Fuels as IBulkOrderFuelLog[]).find(y => y.Id === bulkOrderFuelId)
        const current = (JSON.parse(x.currentValue).Fuels as IBulkOrderFuelLog[]).find(y => y.Id === bulkOrderFuelId)

        if(current && !prev) {
          isChange = true
        }
      }
    })

    return isChange
  }

  return (
    <Flex vertical={true} style={{ width: "max-content" }}>
        {bulkOrder.logs.some(x => x.event === BulkOrderLogEvent.Updated) && !bulkOrder.isPersonalOffer &&
          baseText("Ваш запрос был изменен, ознакомьтесь с изменениями", "warning")}

        {bulkOrder.logs.some(x => x.event === BulkOrderLogEvent.Updated) && bulkOrder.isPersonalOffer &&
          baseText("Предложение было изменено, ознакомьтесь с изменениями", "warning")}

        {baseText(getHeader(bulkOrder.id, bulkOrder.createdOn, bulkOrder.customerName), "secondary")} 
        
        <Text style={{ color: textColor ?? "" }}>
          период c {getValueDate(bulkOrder.startDate, BulkOrderLogChangeType.ChangeStartDate)} по {getValueDate(bulkOrder.endDate, BulkOrderLogChangeType.ChangeEndDate)}
        </Text>
        <Text style={{ color: textColor ?? "" }}>
          Способ доставки: {bulkOrder.deliveryType === DeliveryType.Delivery ? "Доставка" : "Самовывоз"}
        </Text>
        {bulkOrder.fuels.map((x) => (
            <Fragment key={x.id}>
              {
                isNewFuel(x.id) && 
                  <>{getValueNewFuel(x)}</>
              }
              {
                !isNewFuel(x.id) &&
                  <Text style={{ color: textColor ?? "" }}>
                    {/* eslint-disable-next-line max-len */}
                    {getValueDepot()} {getValueFuelType(x.fuel.fuelType, x.id)} {getValueQuantity(x.quantity, x.id)} по цене: {getValueOfferedPrice(x.offeredPrice, x.id)}
                  </Text>
              }
            </Fragment>
        ))}
        
        {depots?.response && fuelTypes?.response && getCreatedFuelLogs().filter(x => !bulkOrder.fuels.map(y => y.id).includes(x.Id)).map((x) => (
          <Fragment key={x.Id}>
            <Text style={{ textDecoration: "line-through" }}>
              {/* eslint-disable-next-line max-len */}
              {depots.response.find(d => d.id === x.DepotId)?.name} {fuelTypes.response.find(d => d.value === x.FuelTypeId)?.label} {x.Quantity}тн по цене: {getPrice(x.OfferedPrice)}
            </Text>
          </Fragment >
        ))}
    </Flex>
  )
}

export default RequestQuoteInfo
